import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { fmtDate } from '@/lib/utils';
import DepositRecognitionModal from '@/components/admin/DepositRecognitionModal';

// Admin "Deposits / Accruals" tab shown in the Sales list. Displays all
// group deposit orders (source 'Group Deposit' or 'Corporate Deposit') with
// their recognition status — DEFERRED (liability nominal) or RECOGNIZED
// (sales revenue) — and the party balance payment status (PENDING / PAID).
// Admins can mark an event complete + record the balance to move the
// deposit from deferred to recognized revenue.
export default function DepositsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | deferred | recognized
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['admin-deposits'],
    queryFn: () => base44.entities.Order.list('-created_date', 500),
  });

  const { data: enquiries = [] } = useQuery({
    queryKey: ['admin-group-bookings'],
    queryFn: () => base44.entities.GroupBookingEnquiry.list('-created_date', 500),
  });

  const enquiryById = useMemo(() => new Map(enquiries.map((e) => [e.id, e])), [enquiries]);

  const depositOrders = useMemo(() => {
    return orders.filter((o) => o.source === 'Group Deposit' || o.source === 'Corporate Deposit' || o.recognition_status);
  }, [orders]);

  const q = search.toLowerCase();
  const filtered = useMemo(() => {
    return depositOrders.filter((o) => {
      if (filter !== 'all' && o.recognition_status !== filter) return false;
      if (!q) return true;
      return [o.customer_name, o.customer_email, o.product_name, o.id].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [depositOrders, q, filter]);

  const counts = useMemo(() => ({
    total: depositOrders.length,
    deferred: depositOrders.filter((o) => o.recognition_status === 'deferred').length,
    recognized: depositOrders.filter((o) => o.recognition_status === 'recognized').length,
    deferredValue: depositOrders.filter((o) => o.recognition_status === 'deferred').reduce((s, o) => s + (Number(o.amount) || 0), 0),
    balancePending: depositOrders.filter((o) => o.source === 'Group Deposit' && o.balance_payment_status !== 'paid').length,
    balancePaid: depositOrders.filter((o) => o.source === 'Group Deposit' && o.balance_payment_status === 'paid').length,
  }), [depositOrders]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-deposits'] });
    queryClient.invalidateQueries({ queryKey: ['admin-group-bookings'] });
    queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
  };

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1';

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="border border-white/5 bg-[#111115] px-4 py-3.5">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">TOTAL DEPOSITS</p>
          <p className="font-black text-xl mt-1 text-white">{counts.total}</p>
        </div>
        <div className="border border-yellow-400/20 bg-yellow-400/5 px-4 py-3.5">
          <p className="text-yellow-400 text-[9px] font-bold tracking-widest">DEFERRED (LIABILITY)</p>
          <p className="font-black text-xl mt-1 text-yellow-400">{counts.deferred}</p>
        </div>
        <div className="border border-green-400/20 bg-green-400/5 px-4 py-3.5">
          <p className="text-green-400 text-[9px] font-bold tracking-widest">RECOGNIZED (REVENUE)</p>
          <p className="font-black text-xl mt-1 text-green-400">{counts.recognized}</p>
        </div>
        <div className="border border-primary/20 bg-primary/5 px-4 py-3.5">
          <p className="text-primary text-[9px] font-bold tracking-widest">DEFERRED VALUE</p>
          <p className="font-black text-xl mt-1 text-primary">£{counts.deferredValue.toFixed(2)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contact, email, package..."
            className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
          />
        </div>
        <div>
          <label className={labelClass}>RECOGNITION</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="seo-input h-11 bg-[#111115] border border-white/10">
            <option value="all">All</option>
            <option value="deferred">Deferred</option>
            <option value="recognized">Recognized</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 border border-white/5 bg-[#111115]">
          <Clock className="w-10 h-10 text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-sm">No group deposit orders found</p>
        </div>
      ) : (
        <div className="border border-white/5 overflow-hidden overflow-x-auto">
          <div className="hidden sm:flex items-center gap-4 px-5 py-3 bg-[#111115] border-b border-white/5 min-w-[860px]">
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 shrink-0">DATE</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-16 shrink-0">SRC</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1">CONTACT</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-24 text-right shrink-0">DEPOSIT</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 text-center shrink-0">BALANCE</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 text-center shrink-0">STATUS</p>
            <p className="w-28 shrink-0" />
          </div>
          {filtered.map((o) => {
            const enquiry = o.linked_enquiry_id ? enquiryById.get(o.linked_enquiry_id) : null;
            const isRecognized = o.recognition_status === 'recognized';
            const isParty = o.source === 'Group Deposit';
            const balancePaid = o.balance_payment_status === 'paid';
            return (
              <div key={o.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/5 hover:bg-white/5 transition-colors min-w-[860px]">
                <p className="text-white/40 text-xs w-20 shrink-0">{fmtDate(o.created_date) || '—'}</p>
                <div className="w-16 shrink-0">
                  <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 ${o.source === 'Corporate Deposit' ? 'text-blue-400 bg-blue-400/10' : 'text-primary bg-primary/10'}`}>
                    {o.source === 'Corporate Deposit' ? 'B2B' : 'B2C'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{o.customer_name || '—'}</p>
                  <p className="text-white/40 text-xs truncate">{o.product_name || o.customer_email || '—'}</p>
                </div>
                <p className="w-24 text-right shrink-0 text-white font-bold text-sm">£{Number(o.amount || 0).toFixed(2)}</p>
                <div className="w-28 text-center shrink-0">
                  {isParty ? (
                    balancePaid ? (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-green-400 bg-green-400/10 px-2 py-0.5">
                        <CheckCircle className="w-3 h-3" /> PAID
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-yellow-400 bg-yellow-400/10 px-2 py-0.5">
                        <Clock className="w-3 h-3" /> PENDING
                      </span>
                    )
                  ) : (
                    <span className="text-white/20 text-[10px]">—</span>
                  )}
                  {balancePaid && o.final_headcount ? (
                    <p className="text-white/30 text-[9px] mt-0.5">{o.final_headcount} drivers</p>
                  ) : enquiry?.estimated_headcount ? (
                    <p className="text-white/30 text-[9px] mt-0.5">est. {enquiry.estimated_headcount}</p>
                  ) : null}
                </div>
                <div className="w-28 text-center shrink-0">
                  {isRecognized ? (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-green-400 bg-green-400/10 px-2 py-0.5">
                      <CheckCircle className="w-3 h-3" /> RECOGNIZED
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-yellow-400 bg-yellow-400/10 px-2 py-0.5">
                      <Clock className="w-3 h-3" /> DEFERRED
                    </span>
                  )}
                </div>
                <div className="w-28 shrink-0 text-right">
                  {!isRecognized && enquiry ? (
                    <button
                      onClick={() => setSelectedEnquiry(enquiry)}
                      className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-primary hover:text-primary/80 transition-colors"
                    >
                      MARK PAID <ArrowRight className="w-3 h-3" />
                    </button>
                  ) : isRecognized ? (
                    <span className="text-white/20 text-[10px]">Done</span>
                  ) : (
                    <span className="text-white/20 text-[10px]">No enquiry</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedEnquiry && (
        <DepositRecognitionModal
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
          onDone={() => { setSelectedEnquiry(null); refresh(); }}
        />
      )}
    </div>
  );
}